// The measures on the Activity tab, in order:
//  1. Lesson Completion
//  2. Training & Assessments Started  (the BE's Learning Range)
//  3. Questions to Elliott            (the BE's Proactive Learning)
//  4. Roleplays                       (lesson Roleplays + Assessment and graded Roleplays; retakes count)
//  5. Practice Rounds                 (practice Roleplays, never graded; every round counts)
// The same five for an admin and a learner. Measures 3 to 5 share one rule.
// Learner: how recently they last did it (7 days Excellent, 14 High, 30
// Moderate, else Low; Exceptional at 3+ in 7 days). Admin: the % of their
// learners who did it at least once in the last 30 days.
// Learning Gaps Closed, Knowledge Retention and Knowledge Mastery are no longer
// Activity measures.
// The first three come from the gamification summaries; Roleplays and
// Practice Rounds from the Capability backend (…/capability/activity, and per
// member in …/capability/members), passed in as `extra`. A measure that
// hasn't arrived is left out, and the rest share the total (teamActivity.ts).

import type { MedalSection } from '../../../../../../components/gamification/scorecardData';
import type { ActivitySectionView } from '../../../../../../api/capability';
import { teamActivity, tierForPercent, type ActivityAudience, type TeamActivity } from './teamActivity';

export const ACTIVITY_MEASURE_ORDER = [
  'Lesson Completion',
  'Learning Range',
  'Proactive Learning',
  'Roleplays',
  'Practice Rounds'
];

/** The three measures scored by recency for a learner (and by % of learners for an admin). */
export const RECENCY_MEASURES = new Set(['Proactive Learning', 'Roleplays', 'Practice Rounds']);

const MEASURE_LINE: Record<string, Record<ActivityAudience, string>> = {
  Roleplays: {
    learner: 'How recently you last completed a Roleplay',
    admin: 'The % of learners who completed a Roleplay in the last 30 days'
  },
  'Practice Rounds': {
    learner: 'How recently you last ran a Practice Roleplay',
    admin: 'The % of learners who ran a Practice Roleplay in the last 30 days'
  }
};

// A learner's recency measures are band positions, not percentages: the same
// labels as Questions to Elliott (useLearnerGamification).
const RECENCY_BAND_LABELS: Record<string, string[]> = {
  Roleplays: ['No Roleplay in 30 days', '15–30 days ago', '8–14 days ago', 'Within 7 days', '3+ Roleplays in 7 days'],
  'Practice Rounds': ['No round in 30 days', '15–30 days ago', '8–14 days ago', 'Within 7 days', '3+ rounds in 7 days']
};
const RECENCY_GATE_LABELS: Record<string, string[]> = {
  Roleplays: ['30d', '14d', '7d', '3 Roleplays'],
  'Practice Rounds': ['30d', '14d', '7d', '3 rounds']
};

/** One of the backend's two new sections in the panel's shape. */
export const sectionFromApi = (s: ActivitySectionView, audience: ActivityAudience): MedalSection => ({
  name: s.name,
  measure: MEASURE_LINE[s.name]?.[audience] ?? '',
  pos: Math.max(0, Math.min(100, s.pct)),
  value: s.value,
  medal: audience === 'learner' ? s.medal : tierForPercent(s.pct),
  boundaries: [25, 50, 75],
  count: s.count,
  ...(audience === 'learner'
    ? { bandLabels: RECENCY_BAND_LABELS[s.name], gateLabels: RECENCY_GATE_LABELS[s.name] }
    : {})
});

/**
 * The five Activity measures in order: the summaries' sections (anything
 * retired dropped) and the Capability backend's Roleplays and Practice Rounds.
 */
export const activitySections = (
  sections: MedalSection[],
  extra: ActivitySectionView[] | null | undefined,
  audience: ActivityAudience = 'admin'
): MedalSection[] => {
  const added = (extra || []).map((s) => sectionFromApi(s, audience));
  return ACTIVITY_MEASURE_ORDER.map(
    (name) => sections.find((s) => s.name === name) ?? added.find((s) => s.name === name)
  ).filter((s): s is MedalSection => Boolean(s));
};

/**
 * A learner's Activity from their medals as the BE sends them, plus their
 * Roleplays and Practice Rounds: their measures, averaged on the learner's
 * tiers. What their own dashboard shows, and what Org Members shows for them,
 * so the two always agree.
 */
export const learnerActivity = (sections: MedalSection[], extra?: ActivitySectionView[] | null): TeamActivity =>
  teamActivity(activitySections(sections, extra, 'learner'), 'learner');
